/**
 * EXTEND (spec 02 §7.7): a letter bubble burst by a player sets one of that player's six slots
 * (routine_3DB0 $3DDD: the bits of $54,x from table $AB53), and a player holding all six gets a life
 * and ends the round (D_32C1, at the end of the main loop's pass), after a show with a screenful of
 * food (L_3389).
 */

import { extendRound } from '../flow';
import { rngOf } from '../random';
import { MAX_LIVES } from '../tuning';
import { systemOf } from './system';
import { EXTEND_FOODS } from './tuning';
import type { PlayerId, World } from '../types';

/**
 * $3DDD: letter n = 0..4 (E X T N D) fills slot n, N and D shifted past the second E (slots 4, 5);
 * an E fills slot 0, or slot 3 once slot 0 is set. Raises `letter` with the slot filled.
 */
export function collectLetter(world: World, id: PlayerId, n: number): void {
  const p = world.players[id];
  let slot = n >= 3 ? n + 1 : n;
  if (slot === 0 && p.extend[0]) slot = 3;
  if (slot < 0 || slot > 5) return;
  p.extend[slot] = true;
  world.events.push({ type: 'letter', player: id, letter: slot });
}

/**
 * D_32C1: player 2, then player 1, with $54,x = $3F - all six - gets a life (up to 7 kept), the
 * letters cleared, and the round ends after the EXTEND show (the flow's `extendRound`), whose screen
 * of food is laid out now.
 */
export function checkExtend(world: World): void {
  for (const id of [1, 0] as const) {
    const p = world.players[id];
    if (!p.extend.every(Boolean)) continue;
    p.lives = Math.min(MAX_LIVES, p.lives + 1);
    p.extend = [false, false, false, false, false, false];
    world.events.push({ type: 'extend', player: id });
    extendRound(world);
    fillWithFood(world);
  }
}

/**
 * L_3389 (02 §7.7 step 2): the show clears the screen and fills it with food, 2x2 tiles each over tile
 * rows 0-23 and all 32 columns, row by row - a random food of the first 32 whose picture is below $28
 * (L_338D draws again), coloured as its own (D_A8E4). They are characters on the screen, not items: the
 * game is stopped for the show and nobody can take them, so they are `bonus` items worth nothing. The
 * C64 shows them `EXTEND_FOOD_FRAMES` after the show begins (the letters scramble first), with nothing
 * else on screen, until the round ends. Laid out once per round.
 */
function fillWithFood(world: World): void {
  const sys = systemOf(world);
  if (sys.extendFood) return;
  sys.extendFood = true;
  const rng = rngOf(world);
  for (let row = 0; row < 24; row += 2) {
    for (let col = 0; col < 32; col += 2) {
      let food = rng.int(256) & 31;
      while (!EXTEND_FOODS.includes(food)) food = rng.int(256) & 31;
      world.items.push({
        id: world.nextId++, kind: 'bonus', item: food, x: col * 8 + 0x14, y: row * 8 + 0x2d, value: 0, life: 0,
        falling: false,
      });
    }
  }
}
